import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Inject, Renderer2 } from '@angular/core';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import {
    ColorOptions,
    HorizontalAlignmentOptions,
    HtmlTagOptions,
    RatioOptions,
    TextStyleOptions,
    UndefinedOption,
    VerticalAlignmentOptions
} from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ShellComponent } from '../../../../ui/display/local-shell.component';
import { TagComponent } from '../../../../ui/display/tag/tag.component';
export interface IPageComponentHeroBannerComponentData extends NICmsContentComponent.IData {
    elements: CategoryImages[];
    equalSlidesHeight?: boolean;
}

let uid = 0;
export interface ICtaElementsData {
    isRegularLink?: boolean;
    cta_copy?: string;
    cta_link?: string;
    anchorId: string;
}
export interface CategoryImages {
    image?: string;
    image_link?: string;
    video_id?: string;
    main_title?: string;
    tag?: string;
    header_copy?: string;
    body_copy?: string;
    cta_elements?: ICtaElementsData[];
    font_color?: string;
    ratio?: string;
    background_color?: string;
    header_copy_style?: string;
    horizontal_align_full_image?: 'left' | 'center' | 'right';
    vertical_align_full_image?: 'top' | 'middle' | 'bottom';
    split_hero_position?: 'left' | 'right';
    text_align_split_hero_left?: 'left' | 'center';
    text_align_split_hero_right?: 'right' | 'center';
    variant?: 'full_image' | 'split_hero' | 'video';
    mobile_text_position?: 'overlay' | 'under_image';
}
const flexClassesOptions = {
    left: 'flex-justify-start',
    center: 'flex-justify-center',
    right: 'flex-justify-end'
};
@Component({
    selector: 'page-component-hero-banner',
    templateUrl: 'hero-banner.html',
    styleUrls: ['hero-banner.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        IfSizeDirective,
        CarouselModule,
        ShellComponent,
        ImageModule,
        YoutubePlayerComponent,
        HrefComponent,
        ICmsClientModule,
        TagComponent,
        ElementHeightDirective,
        ICmsClientModule
    ]
})
@ICmsComponent<PageComponentHeroBannerComponent>({
    id: 'hero-banner',
    name: 'Hero Layout',
    tags: ['Hero', 'Banner'],
    fields: [
        <NICmsContentField.IRepeaterField>{
            key: 'elements',
            title: 'Elements',
            type: NICmsContentField.EFieldType.repeater,
            itemName: 'Item',
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'ratio',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Ratio',
                    options: RatioOptions,
                    description: 'Proportions width x height',
                    default: 'ratio-16-9'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'variant',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Variant',
                    required: true,
                    options: [
                        ...UndefinedOption,
                        { key: 'full_image', title: 'Full image' },
                        { key: 'split_hero', title: 'Split Hero' },
                        { key: 'video', title: 'Video' }
                    ]
                },
                <NICmsContentField.IChoiceField>{
                    key: 'mobile_text_position',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Position text on mobile',
                    required: true,
                    options: [...UndefinedOption, { key: 'overlay', title: 'Overlay' }, { key: 'under_image', title: 'Under image' }]
                },
                <NICmsContentField.IField>{
                    key: 'video_id',
                    type: NICmsContentField.EFieldType.text,
                    title: 'ID or URL',
                    description: 'Video ID or URL',
                    canShow: data => data.variant === 'video'
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    canShow: data => data.variant !== 'video'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'image_link',
                    title: 'Image link'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'main_title',
                    title: 'Main Title'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'tag',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Force Tag',
                    description: 'For SEO reasons you may want to force particular HTML tag',
                    options: [...UndefinedOption, ...HtmlTagOptions]
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'header_copy',
                    title: 'Header copy'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'header_copy_style',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Header Copy Font Style',
                    options: TextStyleOptions,
                    default: 'hero'
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'body_copy',
                    title: 'Body copy'
                },
                <NICmsContentField.IRepeaterField>{
                    key: 'cta_elements',
                    title: 'CTA Elements',
                    type: NICmsContentField.EFieldType.repeater,
                    itemName: 'Item',
                    defaultSize: 1,
                    fields: [
                        {
                            type: NICmsContentField.EFieldType.toggle,
                            key: 'isRegularLink',
                            title: 'Regular Link',
                            default: true
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'cta_copy',
                            title: 'CTA copy'
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'cta_link',
                            title: 'CTA_link',
                            canShow: data => data.isRegularLink
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'anchorId',
                            title: 'CTA Anchor ID',
                            canShow: data => !data.isRegularLink
                        }
                    ]
                },
                <NICmsContentField.IColorPickerField>{
                    key: 'font_color',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Font Colour',
                    options: ColorOptions,
                    colorPreview: true
                },
                <NICmsContentField.IColorPickerField>{
                    key: 'background_color',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Background Colour',
                    options: ColorOptions,
                    colorPreview: true,
                    canShow: data => data.variant === 'split_hero'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'horizontal_align_full_image',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Horizontal Alignnement Options',
                    options: HorizontalAlignmentOptions,
                    default: 'center',
                    canShow: data => data.variant === 'full_image' || data.variant === 'video'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'vertical_align_full_image',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Vertical Alignnement Options',
                    options: VerticalAlignmentOptions,
                    default: 'center',
                    canShow: data => data.variant === 'full_image' || data.variant === 'video'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'split_hero_position',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Split Hero Position',
                    options: [
                        { key: 'left', title: 'Left' },
                        { key: 'right', title: 'Right' }
                    ],
                    default: 'left',
                    canShow: data => data.variant === 'split_hero'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'text_align_split_hero_left',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Text Align',
                    options: [
                        { key: 'left', title: 'Left' },
                        { key: 'center', title: 'Center' }
                    ],
                    default: 'center',
                    canShow: data => data.split_hero_position === 'left' && data.variant === 'split_hero'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'text_align_split_hero_right',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Text Align',
                    options: [
                        { key: 'right', title: 'Right' },
                        { key: 'center', title: 'Center' }
                    ],
                    default: 'center',
                    canShow: data => data.split_hero_position === 'right' && data.variant === 'split_hero'
                }
            ]
        },
        <NICmsContentField.IToggleField>{
            key: 'equalSlidesHeight',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Equal slides height on mobile',
            default: false
        }
    ]
})
export class PageComponentHeroBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentHeroBannerComponentData;

    uid = `pageComponentHeroBanner.${uid++}`;

    constructor(
        @Inject(DOCUMENT)
        private document: Document,
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        private scrollToService: ScrollToService,
        private elementHeightService: ElementHeightService
    ) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(
            this.elementHeightService.getState(this.uid.toLowerCase()).subscribe(value => {
                const top = typeof value !== 'number' ? undefined : value >= 26 ? value - 26 : 0;
                this.elementRef.nativeElement.style.setProperty('--pagination-top', typeof top === 'number' ? `${top}px` : top);
            })
        );
    }
    heroBannerClass(item: CategoryImages): string[] {
        return item.variant === 'full_image' || item.variant === 'video'
            ? [
                  'full_image',
                  item.vertical_align_full_image ? 'v-align-' + item.vertical_align_full_image : '',
                  item.vertical_align_full_image ? 'h-align-' + item.horizontal_align_full_image : ''
              ]
            : [
                  'h-align-' + item.split_hero_position,
                  'v-align-bottom',
                  item.split_hero_position === 'left' ? item.text_align_split_hero_left || '' : item.text_align_split_hero_right || ''
              ];
    }
    isTextMobileUnderImage(item: CategoryImages): boolean {
        return this.client.isS && item.mobile_text_position === 'under_image';
    }
    heroBannerButtonsClass(item: CategoryImages): string | undefined {
        if (item.variant === 'full_image') {
            return (item.horizontal_align_full_image && flexClassesOptions[item.horizontal_align_full_image]) || '';
        } else {
            return item.split_hero_position === 'left'
                ? item.text_align_split_hero_left && flexClassesOptions[item.text_align_split_hero_left]
                : (item.text_align_split_hero_right && flexClassesOptions[item.text_align_split_hero_right]) || '';
        }
    }
    scrollToId(id: string): void {
        const element = this.document.body.querySelector('#' + id);
        if (element) {
            this.scrollToService.scrollTo(element, { offset: 120 });
        }
    }

    trackByFn(index: number, item: any): any {
        return item.id;
    }
}
